import { computed, inject } from 'vue';
import { formatBytes } from '../Attachment/fileKind';
import { formatCompactNumber } from '../Chart/axis';
import { i18nInjectionKey } from '../internal';
import {
  formatRelativeTime,
  type FormatRelativeTimeOptions,
  type NxDateLike,
} from '../RelativeTime/relativeTime';
import {
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatMonth,
  formatTime,
  type NxDateStyle,
  type NxFormatDateBaseOptions,
  type NxFormatDateOptions,
  type NxFormatDateTimeOptions,
  type NxFormatMonthOptions,
  type NxFormatTimeOptions,
} from './date';
import {
  formatHours,
  formatDuration,
  type NxFormatDurationOptions,
  type NxFormatHoursOptions,
} from './duration';
import {
  formatMoney,
  formatNumber,
  formatPercent,
  type NxFormatMoneyOptions,
  type NxFormatNumberOptions,
  type NxNumberLike,
} from './number';

function withLocale<T extends { locale?: string }>(
  styleOrOptions: string | T | undefined,
  locale: string | undefined
): T {
  const options = (
    typeof styleOrOptions === 'string' ? { style: styleOrOptions } : (styleOrOptions ?? {})
  ) as T;
  return options.locale || !locale ? options : { ...options, locale };
}

/**
 * The kit's formatters bound to the app's locale (from `i18nInjectionKey`).
 * The locale is read on every call, so templates follow a language switch.
 * An explicit `locale` in the options still wins. Without an app locale the
 * runtime default applies, exactly like the pure functions.
 */
export function useNxFormat() {
  const i18n = inject(i18nInjectionKey, null);
  const locale = computed(() => i18n?.locale.value || undefined);
  const current = () => locale.value;

  return {
    locale,
    formatDate: (value: NxDateLike, style?: NxDateStyle | NxFormatDateOptions) =>
      formatDate(value, withLocale(style, current())),
    formatDateTime: (value: NxDateLike, style?: NxDateStyle | NxFormatDateTimeOptions) =>
      formatDateTime(value, withLocale(style, current())),
    formatTime: (value: NxDateLike, options?: NxFormatTimeOptions) =>
      formatTime(value, withLocale(options, current())),
    formatMonth: (value: NxDateLike, style?: 'short' | 'long' | NxFormatMonthOptions) =>
      formatMonth(value, withLocale(style, current())),
    formatDayMonth: (value: NxDateLike, options?: NxFormatDateBaseOptions) =>
      formatDayMonth(value, withLocale(options, current())),
    formatRelativeTime: (value: NxDateLike, options?: FormatRelativeTimeOptions) =>
      formatRelativeTime(value, withLocale(options, current())),
    formatNumber: (value: NxNumberLike, options?: NxFormatNumberOptions) =>
      formatNumber(value, withLocale(options, current())),
    formatPercent: (ratio: NxNumberLike, digitsOrOptions?: number | NxFormatNumberOptions) =>
      formatPercent(
        ratio,
        withLocale(
          typeof digitsOrOptions === 'number' ? { digits: digitsOrOptions } : digitsOrOptions,
          current()
        )
      ),
    formatMoney: (amount: NxNumberLike, currency?: string, options?: NxFormatMoneyOptions) =>
      formatMoney(amount, currency, withLocale(options, current())),
    formatHours: (value: NxNumberLike, options?: NxFormatHoursOptions) =>
      formatHours(value, withLocale(options, current())),
    formatDuration: (value: NxNumberLike, options?: NxFormatDurationOptions) =>
      formatDuration(value, withLocale(options, current())),
    formatBytes: (bytes: number | null | undefined) => formatBytes(bytes, current()),
    formatCompactNumber: (value: number) => formatCompactNumber(value, current()),
  };
}

export type NxFormat = ReturnType<typeof useNxFormat>;
